import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Badge, Card, EmptyState, fmtDate } from "@/components/ui-kit";
import { Plus, Search, FileSpreadsheet, FileText as FilePdf, Eye, Trash2, Upload } from "lucide-react";
import { API_BASE } from "@/lib/api";
import BulkImportModal from "@/components/BulkImportModal";

const KOL_CLASSES = ["None", "KOL-A", "KOL-B", "KOL-C"];
const SPECIALTIES = ["Internal Medicine", "Pediatrics", "Cardiology", "Orthopedics", "Gynecology", "Dermatology", "ENT", "Ophthalmology", "General Practice", "Endocrinology", "Neurology", "Psychiatry"];

const emptyDoctor = {
  full_name: "", specialty: "", academic_title: "", gender: "", mobile: "", whatsapp: "",
  email: "", clinic_phone: "", governorate: "", city: "", area: "", address: "",
  latitude: "", longitude: "", clinic_name: "", hospital_affiliations: "",
  working_days: "", working_hours: "", best_visit_timing: "", estimated_patient_volume: "",
  prescription_volume: "", competing_brands: "", products_prescribed: "",
  acceptance_level: "", relationship_strength: "", potential_value: "",
  kol_classification: "None", assistant_name: "", assistant_phone: "",
  assistant_personality: "", communication_style: "", decision_influence: "",
  competitor_products: "", competitor_offers: "", notes: "",
};

export default function Doctors() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ governorate: "", city: "", kol: "" });
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDoctor);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (filter.governorate) params.governorate = filter.governorate;
    if (filter.city) params.city = filter.city;
    if (filter.kol) params.kol = filter.kol;
    const { data } = await api.get("/doctors", { params });
    setList(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [q, filter]);

  const startCreate = () => { setEditing(null); setForm(emptyDoctor); setOpenForm(true); };
  const startEdit = (d) => {
    setEditing(d.id);
    setForm({ ...emptyDoctor, ...d });
    setOpenForm(true);
  };
  const view = async (id) => {
    const { data } = await api.get(`/doctors/${id}`);
    setViewing(data);
    setOpenView(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["latitude", "longitude", "estimated_patient_volume"].forEach(k => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      if (editing) await api.put(`/doctors/${editing}`, payload);
      else await api.post("/doctors", payload);
      setOpenForm(false);
      await load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this doctor?")) return;
    await api.delete(`/doctors/${id}`);
    await load();
  };

  const exportFile = (fmt) => {
    window.open(`${API_BASE}/export/doctors/${fmt}`, "_blank");
  };

  const kolColor = (k) => k === "KOL-A" ? "blue" : k === "KOL-B" ? "purple" : k === "KOL-C" ? "slate" : "slate";

  return (
    <div data-testid="doctors-page">
      <PageHeader
        title={t("doctors")}
        subtitle="Doctor intelligence & KOL management"
        testid="doctors-header"
        actions={
          <>
            <Btn variant="secondary" onClick={() => setBulkOpen(true)} data-testid="bulk-import-doctors">
              <Upload className="w-4 h-4" /> Import
            </Btn>
            <Btn variant="secondary" onClick={() => exportFile("excel")} data-testid="export-excel-doctors">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Btn>
            <Btn variant="secondary" onClick={() => exportFile("pdf")} data-testid="export-pdf-doctors">
              <FilePdf className="w-4 h-4" /> PDF
            </Btn>
            <Btn onClick={startCreate} data-testid="add-doctor-btn">
              <Plus className="w-4 h-4" /> {t("add")}
            </Btn>
          </>
        }
      />

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("search") + "..."}
              data-testid="doctors-search"
              className="block w-full rounded-md border-0 py-1.5 ps-9 pe-3 text-slate-900 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-blue-600 sm:text-sm bg-white"
            />
          </div>
          <Select value={filter.governorate} onChange={(e) => setFilter({ ...filter, governorate: e.target.value })}>
            <option value="">{t("governorate")}</option>
            <option>Gharbia</option><option>Kafr El Sheikh</option>
          </Select>
          <Select value={filter.kol} onChange={(e) => setFilter({ ...filter, kol: e.target.value })}>
            <option value="">{t("kolClass")}</option>
            {KOL_CLASSES.map(k => <option key={k}>{k}</option>)}
          </Select>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500 text-start">
                <th className="py-2 px-3 text-start">{t("fullName")}</th>
                <th className="py-2 px-3 text-start">{t("specialty")}</th>
                <th className="py-2 px-3 text-start">{t("kolClass")}</th>
                <th className="py-2 px-3 text-start">{t("clinicName")}</th>
                <th className="py-2 px-3 text-start">{t("city")}</th>
                <th className="py-2 px-3 text-start">{t("mobile")}</th>
                <th className="py-2 px-3 text-start">Last Visit</th>
                <th className="py-2 px-3 text-end">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-semibold text-slate-900">{d.full_name}</td>
                  <td className="py-2 px-3 text-slate-700">{d.specialty || "-"}</td>
                  <td className="py-2 px-3"><Badge color={kolColor(d.kol_classification)}>{d.kol_classification || "-"}</Badge></td>
                  <td className="py-2 px-3 text-slate-700">{d.clinic_name || "-"}</td>
                  <td className="py-2 px-3 text-slate-700">{d.city || "-"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{d.mobile || "-"}</td>
                  <td className="py-2 px-3 text-slate-500 text-xs">{fmtDate(d.last_visit_date)}</td>
                  <td className="py-2 px-3 text-end">
                    <div className="inline-flex gap-1">
                      <button onClick={() => view(d.id)} className="p-1.5 rounded hover:bg-slate-100" title="View" data-testid={`view-doctor-${d.id}`}><Eye className="w-4 h-4 text-slate-600"/></button>
                      <button onClick={() => startEdit(d)} className="p-1.5 rounded hover:bg-blue-50 text-blue-700 text-xs px-2 font-semibold" data-testid={`edit-doctor-${d.id}`}>{t("edit")}</button>
                      <button onClick={() => remove(d.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-doctor-${d.id}`}><Trash2 className="w-4 h-4 text-red-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <EmptyState message={t("noData")} />}
        </div>
      </Card>

      <Modal open={openForm} onClose={() => setOpenForm(false)} title={editing ? "Edit Doctor" : "Add Doctor"} maxWidth="max-w-4xl">
        <form onSubmit={submit} className="space-y-4" data-testid="doctor-form">
          <div className="overline">Basic Info</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label={t("fullName") + " *"} required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="doctor-name" />
            <Select label={t("specialty")} value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}>
              <option value="">--</option>
              {SPECIALTIES.map(s => <option key={s}>{s}</option>)}
            </Select>
            <Input label="Academic Title" value={form.academic_title} onChange={(e) => setForm({ ...form, academic_title: e.target.value })} />
            <Select label="Gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="">--</option><option>Male</option><option>Female</option>
            </Select>
            <Input label={t("mobile")} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <Input label="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input label="Clinic Phone" value={form.clinic_phone} onChange={(e) => setForm({ ...form, clinic_phone: e.target.value })} />
            <Select label={t("kolClass")} value={form.kol_classification} onChange={(e) => setForm({ ...form, kol_classification: e.target.value })}>
              {KOL_CLASSES.map(k => <option key={k}>{k}</option>)}
            </Select>
          </div>

          <div className="overline pt-2">Location</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label={t("governorate")} value={form.governorate} onChange={(e) => setForm({ ...form, governorate: e.target.value })}>
              <option value="">--</option><option>Gharbia</option><option>Kafr El Sheikh</option>
            </Select>
            <Input label={t("city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <Input label={t("area")} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
            <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="md:col-span-3" />
            <Input label="Latitude" type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            <Input label="Longitude" type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
          </div>

          <div className="overline pt-2">Work & Business</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label={t("clinicName")} value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} />
            <Input label="Hospital Affiliations" value={form.hospital_affiliations} onChange={(e) => setForm({ ...form, hospital_affiliations: e.target.value })} />
            <Input label="Working Days" value={form.working_days} onChange={(e) => setForm({ ...form, working_days: e.target.value })} />
            <Input label="Working Hours" value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} />
            <Input label="Best Visit Timing" value={form.best_visit_timing} onChange={(e) => setForm({ ...form, best_visit_timing: e.target.value })} />
            <Input label="Patient Volume/Day" type="number" value={form.estimated_patient_volume} onChange={(e) => setForm({ ...form, estimated_patient_volume: e.target.value })} />
            <Select label="Acceptance Level" value={form.acceptance_level} onChange={(e) => setForm({ ...form, acceptance_level: e.target.value })}>
              <option value="">--</option><option>high</option><option>medium</option><option>low</option>
            </Select>
            <Select label="Relationship Strength" value={form.relationship_strength} onChange={(e) => setForm({ ...form, relationship_strength: e.target.value })}>
              <option value="">--</option><option>strong</option><option>moderate</option><option>weak</option>
            </Select>
            <Select label="Potential Value" value={form.potential_value} onChange={(e) => setForm({ ...form, potential_value: e.target.value })}>
              <option value="">--</option><option>high</option><option>medium</option><option>low</option>
            </Select>
            <Input label="Competing Brands" value={form.competing_brands} onChange={(e) => setForm({ ...form, competing_brands: e.target.value })} className="md:col-span-3" />
            <Input label="Products Prescribed" value={form.products_prescribed} onChange={(e) => setForm({ ...form, products_prescribed: e.target.value })} className="md:col-span-3" />
          </div>

          <div className="overline pt-2">Assistant / Secretary</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Assistant Name" value={form.assistant_name} onChange={(e) => setForm({ ...form, assistant_name: e.target.value })} />
            <Input label="Assistant Phone" value={form.assistant_phone} onChange={(e) => setForm({ ...form, assistant_phone: e.target.value })} />
            <Input label="Communication Style" value={form.communication_style} onChange={(e) => setForm({ ...form, communication_style: e.target.value })} />
            <Input label="Personality Notes" value={form.assistant_personality} onChange={(e) => setForm({ ...form, assistant_personality: e.target.value })} className="md:col-span-2" />
            <Select label="Decision Influence" value={form.decision_influence} onChange={(e) => setForm({ ...form, decision_influence: e.target.value })}>
              <option value="">--</option><option>high</option><option>medium</option><option>low</option>
            </Select>
          </div>

          <Textarea label={t("notes")} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Btn variant="ghost" type="button" onClick={() => setOpenForm(false)}>{t("cancel")}</Btn>
            <Btn type="submit" disabled={busy} data-testid="doctor-submit">{busy ? t("loading") : t("save")}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={openView} onClose={() => setOpenView(false)} title="Doctor Details" maxWidth="max-w-3xl">
        {viewing && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{viewing.doctor.full_name}</h3>
                <p className="text-sm text-slate-500">{viewing.doctor.specialty} · {viewing.doctor.clinic_name}</p>
              </div>
              <Badge color={kolColor(viewing.doctor.kol_classification)}>{viewing.doctor.kol_classification}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[["Mobile", viewing.doctor.mobile],["WhatsApp", viewing.doctor.whatsapp],["Email", viewing.doctor.email],
                ["Governorate", viewing.doctor.governorate],["City", viewing.doctor.city],["Area", viewing.doctor.area],
                ["Working Days", viewing.doctor.working_days],["Working Hours", viewing.doctor.working_hours],
                ["Patient Volume", viewing.doctor.estimated_patient_volume],
                ["Acceptance", viewing.doctor.acceptance_level],["Assistant", viewing.doctor.assistant_name]
              ].map(([l, v]) => (
                <div key={l}><div className="text-xs text-slate-500 uppercase">{l}</div><div className="font-semibold text-slate-800">{v || "-"}</div></div>
              ))}
            </div>
            <div>
              <div className="overline mb-2">Recent Visits ({viewing.visits.length})</div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {viewing.visits.map((v) => (
                  <div key={v.id} className="border border-slate-200 rounded p-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{v.rep_name}</span>
                      <span className="text-xs text-slate-500">{fmtDate(v.created_at)}</span>
                    </div>
                    <div className="text-slate-600 text-xs">{v.notes || "-"}</div>
                  </div>
                ))}
                {viewing.visits.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No visits yet</div>}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <BulkImportModal entity="doctors" open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={load} />
    </div>
  );
}
